import * as React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Pagination,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { toast } from "react-toastify";
import {
  getMedia,
  getMediasError,
  getMediaStatus,
  selectAllMedias,
  fetchTargetTypes,
  selectTargetTypes,
  selectCurrentPage,
  selectTotalCount,
  updateMedia,
} from "../../features/mediaSlice";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../components/loading";
import Error from "../../components/Error";
import AppBarSearch from "../../global/appBarSearch";
import MediaPreviewDialog from "../../components/mediaPreviewDialog";
import { ResponsiveProvider } from "../../hooks/uiHook";
import RestaurantTopBar from "../../components/restaurantTopBar";
import MenuItemFilter from "../../components/filters/menuItemFilter";
import NoData from "../../components/noData";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { useRestaurant } from "../../hooks/RestaurantContext";

const apiUrl = process.env.REACT_APP_MEDIA_URL;
const PAGE_LIMIT = 24;

const isVideo = (item) => {
  const mime = item.mimeType || item.mimetype || "";
  if (mime.startsWith("video/")) return true;
  if (item.type === "carousel" || item.type === "video") {
    return String(item.url || "").toLowerCase().endsWith(".mp4");
  }
  return false;
};

const mediaUrl = (item) =>
  item?.url ? `${apiUrl}/${String(item.url).replace(/\\/g, "/")}` : "";

const formatSize = (bytes) => {
  if (!bytes || typeof bytes !== "number") return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Media = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const status = useSelector(getMediaStatus);
  const error = useSelector(getMediasError);
  const mediaItems = useSelector(selectAllMedias);
  const targetTypes = useSelector(selectTargetTypes);
  const currentPage = useSelector(selectCurrentPage);
  const totalCount = useSelector(selectTotalCount);
  const [selectedTargetType, setSelectedTargetType] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const totalPages = Math.ceil(totalCount / PAGE_LIMIT);

  const openPreview = (item) => {
    setPreview(item);
  };

  const handleRename = async (trimmed) => {
    try {
      await dispatch(
        updateMedia({
          id: preview._id,
          filename: trimmed,
          restaurantId: restaurantId || preview?.restaurantId,
        })
      ).unwrap();
      setPreview((prev) => (prev ? { ...prev, filename: trimmed } : prev));
      toast.success(t("media.renameSuccess"));
    } catch (err) {
      toast.error(err?.message || t("media.renameError"));
      throw err;
    }
  };

  const fetchMedia = React.useCallback(
    (page = 1, targetType = "", replace = false) => {
      dispatch(
        getMedia({
          page,
          limit: PAGE_LIMIT,
          targetType,
          replace,
          restaurantId,
        })
      );
    },
    [dispatch, restaurantId]
  );

  const handlePageChange = (_event, value) => {
    fetchMedia(value, selectedTargetType, true);
  };

  const handleFilterChange = React.useCallback(
    (event) => {
      const newTargetType = event.target.value;
      setSelectedTargetType(newTargetType);
      fetchMedia(1, newTargetType, true);
    },
    [fetchMedia]
  );

  React.useEffect(() => {
    dispatch(fetchTargetTypes());
    fetchMedia(1, "", true);
  }, [dispatch, fetchMedia]);

  const typeLabel = (value) => {
    if (!value) return value;
    const exactKey = `mediaPicker.${value}`;
    if (i18n.exists(exactKey)) return t(exactKey);
    // Some older media were saved with a lowercase targetType (e.g.
    // "product") while the translations are keyed by the capitalized
    // model name (e.g. "Product") — try that before giving up.
    const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
    const capitalizedKey = `mediaPicker.${capitalized}`;
    if (i18n.exists(capitalizedKey)) return t(capitalizedKey);
    return capitalized;
  };

  return (
    <div className="main-application">
      <RestaurantTopBar />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          title={t("pages.myMedia")}
          children={
            <MenuItemFilter
              label={t("fields.targetType")}
              emptyLabel={t("filters.all")}
              value={selectedTargetType}
              onChange={handleFilterChange}
              filters={(targetTypes || [])
                .filter((item) => item.value)
                .map((item) => ({
                  _id: item.value,
                  label: typeLabel(item.value) || item.label,
                }))}
            />
          }
          actions={
            <>
              <Button
                variant="contained"
                color="info"
                startIcon={<EditIcon />}
                onClick={() => navigate("modify")}
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("common.edit")}
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => navigate("add")}
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("actions.addMedia")}
              </Button>
            </>
          }
        />
      </ResponsiveProvider>
      <main>
        <Box sx={{ my: 2 }}>
          {status === "loading" ? (
            <Loading />
          ) : status === "failed" ? (
            <Error>{error}</Error>
          ) : mediaItems && mediaItems.length > 0 ? (
            <Grid container spacing={2}>
              {mediaItems.map((item) => {
                const video = isVideo(item);
                const src = mediaUrl(item);
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={item._id}>
                    <Card
                      onClick={() => openPreview({ ...item, src, video })}
                      sx={{
                        height: "100%",
                        cursor: "pointer",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        transition: "border-color 0.2s",
                        "&:hover": { borderColor: "primary.main" },
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          height: 150,
                          bgcolor: "action.hover",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {src ? (
                          video ? (
                            <video
                              src={src}
                              muted
                              preload="metadata"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={src}
                              alt={item.filename || t("media.untitled")}
                              loading="lazy"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          )
                        ) : (
                          <ImageOutlinedIcon
                            sx={{ fontSize: 36, color: "text.secondary" }}
                          />
                        )}
                        {video && (
                          <PlayCircleOutlineIcon
                            sx={{
                              position: "absolute",
                              fontSize: 42,
                              color: "#fff",
                              filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.6))",
                            }}
                          />
                        )}
                        <Chip
                          size="small"
                          icon={
                            video ? (
                              <VideocamOutlinedIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <ImageOutlinedIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          label={video ? t("media.video") : t("media.image")}
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            fontWeight: 700,
                            bgcolor: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            "& .MuiChip-icon": { color: "#fff" },
                          }}
                        />
                      </Box>
                      <Box sx={{ px: 1.25, py: 1.25, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 3,
                            overflow: "hidden",
                            wordBreak: "break-word",
                          }}
                        >
                          {item.filename || t("media.untitled")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {[
                            typeLabel(item.targetType || item.type),
                            item.scope === "restaurant" ? t("media.exclusiveBadge") : t("media.shared"),
                            formatSize(item.size),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <NoData />
          )}
        </Box>
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage || 1}
              onChange={handlePageChange}
              disabled={status === "loading"}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </main>

      <MediaPreviewDialog
        preview={preview}
        onClose={() => setPreview(null)}
        canRename
        onRename={handleRename}
      />
    </div>
  );
};

export default Media;
